// Competition master-schedule scan + home-studio extraction.
//
// A competition publishes ONE master program listing every studio's routines in
// performance order. A studio needs to find ITS OWN routines in that program.
// This module does the studio-agnostic core: parse the delimited schedule text,
// determine which entries belong to the home studio (canonical name + known
// aliases, abbreviation-aware, no false positives on similar names), and
// organize them in performance order.
//
// Input text comes from: a pasted/uploaded CSV (Option A), a PDF text layer
// extracted in an Edge Function (Option C), or Textract table output (Option B).
// All three funnel into parseCompetitionSchedule() -> extractHomeRoutines().

import type { ImportError, ImportSummary } from '../import/errors';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ScheduleEntry {
  /** Performance/run order as printed, e.g. "001". May be empty if absent. */
  entryNumber: string;
  day: string | null;
  time: string | null;
  title: string;
  /** The studio name exactly as printed in the program (un-normalized). */
  studioPrinted: string;
  category: string | null; // dance type/genre
  size: string | null; // Solo / Duo-Trio / Group / Line / Production
  numDancers: number | null;
  ageDivision: string | null;
  level: string | null;
  /** Parsed from "m:ss" or a raw seconds value; null if unparseable/absent. */
  durationSeconds: number | null;
  durationRaw: string | null;
}

export interface HomeStudioConfig {
  /** The studio's primary name. */
  canonical: string;
  /** Every other spelling the studio appears under in programs (incl. canonical is fine). */
  aliases: string[];
}

export interface ScheduleScanResult {
  summary: ImportSummary;
  totalEntries: number;
  /** Distinct studio-name STRINGS in the program (our studio may print many ways). */
  distinctNameStrings: number;
  /** Distinct ACTUAL studios after collapsing our aliases to one. */
  distinctActualStudios: number;
  /** The home-studio spellings that actually appeared in this program. */
  matchedAliases: string[];
  /** The home studio's routines, in performance order, fully detailed. */
  homeRoutines: ScheduleEntry[];
  /** All parsed entries (host of homeRoutines), for preview/debugging. */
  entries: ScheduleEntry[];
}

// ---------------------------------------------------------------------------
// Studio-name normalization + home matcher
// ---------------------------------------------------------------------------

const STUDIO_ABBREVIATIONS: readonly (readonly [RegExp, string])[] = [
  [/\bda\b/g, 'dance academy'],
  [/\bdc\b/g, 'dance company'],
  [/\bdco\b/g, 'dance company'],
  [/\bacad\b/g, 'academy'],
  [/\bco\b/g, 'company'],
  [/\bctr\b/g, 'center'],
  [/\bcentre\b/g, 'center'],
  [/\bperf\b/g, 'performing'],
  [/\bsch\b/g, 'school'],
];

/**
 * Canonicalize a printed studio name for comparison: lowercase, expand common
 * abbreviations as whole tokens, strip punctuation, collapse whitespace.
 */
export function normalizeStudioName(raw: string): string {
  let s = ` ${raw.toLowerCase()} `;
  s = s.replace(/&/g, ' and ');
  s = s.replace(/[.,'’`\-_/]/g, ' '); // punctuation -> space (so "acad." -> "acad")
  s = s.replace(/[^a-z0-9 ]/g, ' ');
  s = s.replace(/\s+/g, ' ');
  for (const [re, rep] of STUDIO_ABBREVIATIONS) s = s.replace(re, ` ${rep} `);
  return s.replace(/\s+/g, ' ').trim();
}

export interface HomeStudioMatcher {
  normalizedAliases: string[];
  isHome(printedName: string): boolean;
}

/**
 * Build a matcher for the home studio. Matching is EXACT on the normalized form
 * (canonical + every registered alias). A shared first word like "Allegro" is
 * deliberately NOT enough — the whole normalized name must equal a registered
 * spelling — which is what prevents false matches on similarly-named studios.
 */
export function buildHomeStudioMatcher(home: HomeStudioConfig): HomeStudioMatcher {
  const set = new Set<string>(
    [home.canonical, ...home.aliases].map(normalizeStudioName).filter((s) => s.length > 0),
  );
  return {
    normalizedAliases: [...set],
    isHome(printedName: string): boolean {
      return set.has(normalizeStudioName(printedName));
    },
  };
}

// ---------------------------------------------------------------------------
// Parsing delimited schedule text
// ---------------------------------------------------------------------------

// Map a normalized header token to one of our canonical fields. First match wins.
const HEADER_SYNONYMS: Record<keyof Omit<ScheduleEntry, 'durationSeconds'>, string[]> = {
  entryNumber: ['entry', 'entry_number', 'entry_no', 'entryno', 'no', 'number', 'num', '#'],
  day: ['day', 'date', 'session'],
  time: ['time', 'scheduled_time', 'performance_time', 'perf_time', 'start_time'],
  title: [
    'title',
    'routine',
    'routine_title',
    'routine_name',
    'dance',
    'dance_name',
    'name',
    'entry_name',
  ],
  studioPrinted: ['studio', 'studio_name', 'school', 'company', 'team', 'organization', 'org'],
  category: ['category', 'genre', 'style', 'type', 'dance_type', 'discipline'],
  size: ['size', 'size_category', 'entry_type', 'category_size', 'group_size'],
  numDancers: [
    'dancers',
    'num_dancers',
    'numdancers',
    'count',
    'performers',
    'number_of_dancers',
    'no_of_dancers',
  ],
  ageDivision: ['age', 'age_division', 'division', 'age_group', 'agediv'],
  level: ['level', 'skill_level', 'competition_level', 'tier'],
  durationRaw: ['duration', 'length', 'length_seconds', 'song_length', 'time_length', 'run_time'],
};

function normalizeHeader(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_#]/g, '');
}

function detectDelimiter(headerLine: string): string {
  if (headerLine.includes('\t')) return '\t';
  if (headerLine.includes('|')) return '|';
  return ',';
}

/** Parse "m:ss", "mm:ss", or a plain seconds integer into seconds. */
export function parseDurationToSeconds(raw: string): number | null {
  const v = raw.trim();
  if (v === '') return null;
  const mmss = /^(\d{1,2}):([0-5]?\d)$/.exec(v);
  if (mmss) return Number(mmss[1]) * 60 + Number(mmss[2]);
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export interface CompetitionScheduleParse {
  summary: ImportSummary;
  entries: ScheduleEntry[];
}

/** Hard cap on input size (~5 MB) — bounds split()/regex work on hostile input. */
export const MAX_SCHEDULE_INPUT_CHARS = 5_000_000;

/**
 * Parse a delimited competition program into structured entries. Accepts
 * tab/pipe/comma delimiters and flexible column headers (mapped via synonyms).
 * The only hard requirements are a `studio` column and a `title` column — those
 * are what make the home-studio scan possible.
 */
export function parseCompetitionSchedule(text: string): CompetitionScheduleParse {
  const errors: ImportError[] = [];
  const warnings: ImportError[] = [];
  const entries: ScheduleEntry[] = [];

  const cleaned = text
    .replace(/^\uFEFF/, '')
    .replace(/\0/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .trim();
  if (cleaned.length === 0) {
    return { summary: { totalRows: 0, validRows: 0, invalidRows: 0, errors, warnings }, entries };
  }
  if (cleaned.length > MAX_SCHEDULE_INPUT_CHARS) {
    errors.push({
      row: 0,
      column: 'file',
      kind: 'value_out_of_range',
      message: 'The schedule is too large (max ~5 MB). Split it or paste fewer rows.',
    });
    return { summary: { totalRows: 0, validRows: 0, invalidRows: 0, errors, warnings }, entries };
  }

  const lines = cleaned.split('\n').filter((l) => l.trim() !== '');
  const headerLine = lines[0] ?? '';
  const delimiter = detectDelimiter(headerLine);
  const headers = headerLine.split(delimiter).map(normalizeHeader);

  // Resolve each canonical field to a column index (or -1).
  const fieldIndex = {} as Record<keyof typeof HEADER_SYNONYMS, number>;
  for (const field of Object.keys(HEADER_SYNONYMS) as (keyof typeof HEADER_SYNONYMS)[]) {
    fieldIndex[field] = headers.findIndex((h) => HEADER_SYNONYMS[field].includes(h));
  }

  if (fieldIndex.studioPrinted < 0) {
    errors.push({
      row: 0,
      column: 'studio',
      kind: 'header_missing',
      message: 'A "studio" column is required to determine which routines are yours.',
    });
  }
  if (fieldIndex.title < 0) {
    errors.push({
      row: 0,
      column: 'title',
      kind: 'header_missing',
      message: 'A "title"/"routine" column is required.',
    });
  }
  if (errors.length > 0) {
    return {
      summary: {
        totalRows: lines.length - 1,
        validRows: 0,
        invalidRows: lines.length - 1,
        errors,
        warnings,
      },
      entries,
    };
  }

  const cell = (cells: string[], field: keyof typeof HEADER_SYNONYMS): string => {
    const i = fieldIndex[field];
    return i >= 0 ? (cells[i] ?? '').trim() : '';
  };

  for (let i = 1; i < lines.length; i++) {
    const rowNum = i + 1;
    const cells = (lines[i] ?? '').split(delimiter).map((c) => c.trim());
    const studioPrinted = cell(cells, 'studioPrinted');
    const title = cell(cells, 'title');
    if (studioPrinted === '' || title === '') {
      warnings.push({
        row: rowNum,
        column: studioPrinted === '' ? 'studio' : 'title',
        kind: 'row_invalid',
        message: 'Skipped row missing studio or title.',
      });
      continue;
    }
    const numRaw = cell(cells, 'numDancers');
    const num = Number.parseInt(numRaw, 10);
    const durationRaw = cell(cells, 'durationRaw');
    entries.push({
      entryNumber: cell(cells, 'entryNumber'),
      day: cell(cells, 'day') || null,
      time: cell(cells, 'time') || null,
      title,
      studioPrinted,
      category: cell(cells, 'category') || null,
      size: cell(cells, 'size') || null,
      numDancers: Number.isFinite(num) && num >= 0 ? num : null,
      ageDivision: cell(cells, 'ageDivision') || null,
      level: cell(cells, 'level') || null,
      durationSeconds: durationRaw ? parseDurationToSeconds(durationRaw) : null,
      durationRaw: durationRaw || null,
    });
  }

  return {
    summary: {
      totalRows: lines.length - 1,
      validRows: entries.length,
      invalidRows: lines.length - 1 - entries.length,
      errors,
      warnings,
    },
    entries,
  };
}

// ---------------------------------------------------------------------------
// Extract + organize the home studio's routines
// ---------------------------------------------------------------------------

function performanceOrder(a: ScheduleEntry, b: ScheduleEntry): number {
  const na = Number.parseInt(a.entryNumber, 10);
  const nb = Number.parseInt(b.entryNumber, 10);
  if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb;
  // fall back to (time) when entry numbers are absent/equal
  if (a.time && b.time && a.time !== b.time) return a.time.localeCompare(b.time);
  return 0;
}

/**
 * Given parsed entries and the home studio config, return the home studio's
 * routines in performance order plus a summary of what was resolved.
 */
export function extractHomeRoutines(
  entries: ScheduleEntry[],
  home: HomeStudioConfig,
): Omit<ScheduleScanResult, 'summary' | 'entries'> {
  const matcher = buildHomeStudioMatcher(home);
  const homeRoutines = entries
    .filter((e) => matcher.isHome(e.studioPrinted))
    .sort(performanceOrder);
  const matchedAliases = [...new Set(homeRoutines.map((e) => e.studioPrinted))];
  const distinctNameStrings = new Set(entries.map((e) => e.studioPrinted)).size;
  const distinctActualStudios =
    distinctNameStrings - matchedAliases.length + (homeRoutines.length > 0 ? 1 : 0);
  return {
    totalEntries: entries.length,
    distinctNameStrings,
    distinctActualStudios,
    matchedAliases,
    homeRoutines,
  };
}

/** One-shot: parse the program text and extract the home studio's routines. */
export function scanCompetitionSchedule(text: string, home: HomeStudioConfig): ScheduleScanResult {
  const { summary, entries } = parseCompetitionSchedule(text);
  const extracted = extractHomeRoutines(entries, home);
  return { summary, entries, ...extracted };
}
